import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Truck, Zap, Tractor, Wrench, Search, Leaf } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  "Trailers": Truck,
  "Generators": Zap,
  "Tractors": Tractor,
  "Tractor Attachments": Wrench,
  "Lawn Care": Leaf,
};

const ALL_CATEGORIES = ["all", "Trailers", "Generators", "Tractors", "Tractor Attachments", "Lawn Care"];

export default function EquipmentCatalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const activeCategory = searchParams.get("category") || "all";

  useEffect(() => {
    const fetchEquipment = async () => {
      setLoading(true);
      try {
        const params = activeCategory !== "all" ? `?category=${encodeURIComponent(activeCategory)}` : "";
        const { data } = await axios.get(`${API}/equipment${params}`);
        setEquipment(data);
      } catch (err) {
        console.error("Failed to fetch equipment:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEquipment();
  }, [activeCategory]);

  const filtered = equipment.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCategoryClick = (cat) => {
    if (cat === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  return (
    <div data-testid="equipment-catalog" className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <div className="bg-[#1A2F24] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="catalog-heading"
          >
            Our Equipment
          </h1>
          <p className="text-white/60 text-base">Find the right tool for your project</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Search + Filters */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {ALL_CATEGORIES.map(cat => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => handleCategoryClick(cat)}
                className={`category-pill px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  activeCategory === cat
                    ? "active bg-[#E68A00] text-white border-[#E68A00]"
                    : "bg-white text-[#595959] border-[#E5E0D8] hover:border-[#E68A00]"
                }`}
              >
                {cat === "all" ? "All Equipment" : cat}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#595959]" />
            <Input
              data-testid="equipment-search"
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-[#E5E0D8] focus:ring-[#E68A00] focus:border-[#E68A00]"
            />
          </div>
        </div>

        {/* Equipment Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-[#E68A00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#595959] text-lg">No equipment found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => {
              const Icon = CATEGORY_ICONS[item.category] || Truck;
              return (
                <Link
                  key={item.id}
                  to={`/equipment/${item.id}`}
                  data-testid={`equipment-card-${item.id}`}
                  className="equipment-card bg-white border border-[#E5E0D8] rounded-lg overflow-hidden group"
                >
                  <div className="relative h-48 bg-[#EFECE5] overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                        item.available ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}>
                        {item.available ? "Available" : "Rented Out"}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-[#E68A00]" />
                      <span className="text-xs font-medium text-[#595959] uppercase tracking-wider">{item.category}</span>
                    </div>
                    <h3
                      className="text-lg font-semibold text-[#1C1C1C] mb-2 group-hover:text-[#E68A00] transition-colors"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {item.name}
                    </h3>
                    <p className="text-sm text-[#595959] line-clamp-2 mb-4">{item.description}</p>
                    <div className="flex items-baseline gap-3 border-t border-[#E5E0D8] pt-3">
                      <div>
                        <span className="text-xl font-bold text-[#1A2F24]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          ${item.daily_rate}
                        </span>
                        <span className="text-xs text-[#595959] ml-1">/day</span>
                      </div>
                      <div className="text-sm text-[#595959]">
                        ${item.weekly_rate}<span className="text-xs">/week</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
